import React from "react";
import { fmtTs, fmtINR, stepLabel } from "./helpers";

// Normal pipeline stages in order (codes matching to_status in lifecycle steps)
var NORMAL_PIPELINE = [
  { code: 40,  label: "Cash Collected" },
  { code: 70,  label: "Handover Requested" },
  { code: 80,  label: "Handover Approved" },
  { code: 90,  label: "Deposit Requested" },
  { code: 100, label: "Bank Deposited" },
  { code: 110, label: "Reconciled" },
];

// Stages that represent terminal / reversal end-states — skip not-started placeholders
var TERMINAL_CODES = [100, 110, 99];
// Stages that render as a special red alert event
var REVERSAL_CODES = [99, 55];

// Build a resolved name for a lifecycle actor
function actorName(userId, userName) {
  if (userId === 0 || userName === "User 0") return "Bank / System";
  return userName || ("User " + userId);
}

// ── Render helpers ───────────────────────────────────────────────────────────

function StepSwap({ step, isLast }) {
  var pairs = step.swap_pairs || [];
  return (
    <div className="tm-lifecycle__step">
      <div className="tm-lifecycle__spine">
        <div className="tm-lifecycle__adj-icon tm-lifecycle__adj-icon--swap">
          <span>↔</span>
        </div>
        {!isLast && <div className="tm-lifecycle__line tm-lifecycle__line--swap" />}
      </div>
      <div className="tm-lifecycle__content">
        <div className="tm-lifecycle__title">
          Loan Swap
          <span className="tm-chip tm-chip--yellow ml-6">Swap</span>
        </div>
        {pairs.map(function(pair, idx) {
          return (
            <div key={idx} className="tm-lifecycle__sub">
              ₹{fmtINR(pair.amount)} moved from {pair.from_loan} → {pair.to_loan}
            </div>
          );
        })}
        <div className="tm-lifecycle__sub">By {step.from_user_name || "Operations Team"}</div>
        {step.transfer_remark && (
          <div className="tm-lifecycle__remark tm-lifecycle__remark--swap">
            Remark: {step.transfer_remark}
          </div>
        )}
        <div className="tm-lifecycle__time">{fmtTs(step.time_stamp)}</div>
      </div>
    </div>
  );
}

function StepReversal({ step, isLast }) {
  var fromName = actorName(step.from_user, step.from_user_name);
  var toName   = actorName(step.to_user,   step.to_user_name);
  return (
    <div className="tm-lifecycle__step">
      <div className="tm-lifecycle__spine">
        <div className="tm-lifecycle__adj-icon tm-lifecycle__adj-icon--reversal">
          <span>⚠</span>
        </div>
        {!isLast && <div className="tm-lifecycle__line tm-lifecycle__line--reversal" />}
      </div>
      <div className="tm-lifecycle__content">
        <div className="tm-lifecycle__title">
          {stepLabel(step)}
          <span className="tm-chip tm-chip--red ml-6">Reversal</span>
        </div>
        <div className="tm-lifecycle__sub">{fromName} → {toName}</div>
        {step.transfer_remark && (
          <div className="tm-lifecycle__remark tm-lifecycle__remark--reversal">
            {step.transfer_remark}
          </div>
        )}
        <div className="tm-lifecycle__time">{fmtTs(step.time_stamp)}</div>
      </div>
    </div>
  );
}

function StepFailed({ step, isLast }) {
  var fromName = actorName(step.from_user, step.from_user_name);
  var toName   = actorName(step.to_user,   step.to_user_name);
  return (
    <div className="tm-lifecycle__step">
      <div className="tm-lifecycle__spine">
        <div className="tm-lifecycle__icon tm-lifecycle__icon--failed">
          <span>✕</span>
        </div>
        {!isLast && <div className="tm-lifecycle__line tm-lifecycle__line--reversal" />}
      </div>
      <div className="tm-lifecycle__content">
        <div className="tm-lifecycle__title">
          {stepLabel(step)}
          <span className="tm-chip tm-chip--red ml-6">Failed</span>
        </div>
        <div className="tm-lifecycle__sub">{fromName} → {toName}</div>
        {step.transfer_remark && (
          <div className="tm-lifecycle__remark tm-lifecycle__remark--reversal">
            {step.transfer_remark}
          </div>
        )}
        <div className="tm-lifecycle__time">{fmtTs(step.time_stamp)}</div>
      </div>
    </div>
  );
}

function StepNormal({ step, isPending, isLast }) {
  var fromName = actorName(step.from_user, step.from_user_name);
  var toName   = actorName(step.to_user,   step.to_user_name);
  var iconCls  = isPending ? "tm-lifecycle__icon icon--pending icon--ring"
                           : "tm-lifecycle__icon icon--done";
  var lineCls  = isPending ? "tm-lifecycle__line tm-lifecycle__line--pending"
                           : "tm-lifecycle__line";
  return (
    <div className="tm-lifecycle__step">
      <div className="tm-lifecycle__spine">
        <div className={iconCls}>{isPending ? <span>⏱</span> : <span>✓</span>}</div>
        {!isLast && <div className={lineCls} />}
      </div>
      <div className="tm-lifecycle__content">
        <div className="tm-lifecycle__title">{stepLabel(step)}</div>
        <div className="tm-lifecycle__sub">{fromName} → {toName}</div>
        {step.transfer_remark && (
          <div className="tm-lifecycle__remark">{step.transfer_remark}</div>
        )}
        <div className="tm-lifecycle__time">{fmtTs(step.time_stamp)}</div>
      </div>
    </div>
  );
}

function StageNotStarted({ label, isLast }) {
  return (
    <div className="tm-lifecycle__step tm-lifecycle__step--not-started">
      <div className="tm-lifecycle__spine">
        <div className="tm-lifecycle__icon tm-lifecycle__icon--not-started">
          <span>○</span>
        </div>
        {!isLast && <div className="tm-lifecycle__line tm-lifecycle__line--not-started" />}
      </div>
      <div className="tm-lifecycle__content">
        <div className="tm-lifecycle__title tm-lifecycle__title--not-started">{label}</div>
        <div className="tm-lifecycle__sub tm-lifecycle__sub--not-started">Not yet reached</div>
      </div>
    </div>
  );
}

// ── Main component ───────────────────────────────────────────────────────────

export default function LifecycleTimeline({ steps, txnStatusCode }) {
  // No steps yet — show full not-started pipeline
  if (!steps || steps.length === 0) {
    return (
      <div className="tm-lifecycle">
        {NORMAL_PIPELINE.map(function(stage, idx) {
          return (
            <StageNotStarted
              key={stage.code}
              label={stage.label}
              isLast={idx === NORMAL_PIPELINE.length - 1}
            />
          );
        })}
      </div>
    );
  }

  // Build set of to_status codes that already appear in steps
  var visitedCodes = {};
  steps.forEach(function(s) { visitedCodes[s.to_status] = true; });

  var isTerminal = TERMINAL_CODES.indexOf(txnStatusCode) !== -1;
  var hasReversal = visitedCodes[99] || visitedCodes[55];

  // Determine which NORMAL_PIPELINE stages are still "not started"
  // (skip for reversed / terminal transactions — the pipeline is closed)
  var notStartedStages = [];
  if (!isTerminal && !hasReversal) {
    notStartedStages = NORMAL_PIPELINE.filter(function(stage) {
      return !visitedCodes[stage.code];
    });
  }

  var totalItems = steps.length + notStartedStages.length;

  return (
    <div className="tm-lifecycle">
      {steps.map(function(step, idx) {
        var isLast   = idx === steps.length - 1 && notStartedStages.length === 0;
        var code     = step.to_status;

        if (step.is_failed) {
          return <StepFailed key={idx} step={step} isLast={isLast} />;
        }
        if (step.step_type === "LOAN_SWAP") {
          return <StepSwap key={idx} step={step} isLast={isLast} />;
        }
        if (REVERSAL_CODES.indexOf(code) !== -1) {
          return <StepReversal key={idx} step={step} isLast={isLast} />;
        }

        // Last real step and not terminal → currently pending
        var isPending = idx === steps.length - 1 &&
          TERMINAL_CODES.indexOf(txnStatusCode) === -1 &&
          !hasReversal;

        return (
          <StepNormal key={idx} step={step} isPending={isPending} isLast={isLast} />
        );
      })}

      {notStartedStages.map(function(stage, idx) {
        return (
          <StageNotStarted
            key={"ns-" + stage.code}
            label={stage.label}
            isLast={steps.length + idx === totalItems - 1}
          />
        );
      })}
    </div>
  );
}
